import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getBotSettings, updateBotSettings, type PaymentMethodConfig } from '@/lib/api/botSettings'
import { useAuthStore } from '@/store/authStore'
import {
  hasPaymentErrors,
  newMethodDraft,
  toMethodDrafts,
  toMethodPayload,
  validatePaymentSettings,
  type PaymentMethodDraft,
  type PaymentSettingsErrors,
} from '@/lib/paymentMethods'
import { PaymentSettingsView } from './PaymentSettingsView'

interface SavedPaymentSettings {
  methods: PaymentMethodConfig[]
  shareDetails: boolean
}

const paymentSettingsKey = (tenantId: string | null) => ['bot-settings', tenantId, 'payments'] as const

function saveErrorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { error?: string } } } | null)?.response
  return response?.data?.error || 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.'
}

interface PaymentSettingsEditorProps {
  saved: SavedPaymentSettings
  tenantId: string | null
}

/** Edita un borrador de lo guardado. Se vuelve a montar (por `key`) cuando cambia lo guardado, así no hay copias viejas. */
function PaymentSettingsEditor({ saved, tenantId }: PaymentSettingsEditorProps) {
  const queryClient = useQueryClient()
  const [methods, setMethods] = useState<PaymentMethodDraft[]>(() => toMethodDrafts(saved.methods))
  const [shareDetails, setShareDetails] = useState(saved.shareDetails)
  const [errors, setErrors] = useState<PaymentSettingsErrors>({ methods: {} })

  const isDirty =
    shareDetails !== saved.shareDetails ||
    JSON.stringify(toMethodPayload(methods)) !== JSON.stringify(toMethodPayload(toMethodDrafts(saved.methods)))

  const mutation = useMutation({
    mutationFn: () =>
      updateBotSettings({ checkoutConfig: { paymentMethods: toMethodPayload(methods), sharePaymentDetails: shareDetails } }),
    onSuccess: () => {
      toast.success('Configuración de pagos guardada')
      void queryClient.invalidateQueries({ queryKey: paymentSettingsKey(tenantId) })
    },
  })

  const handleSave = () => {
    const found = validatePaymentSettings(methods, shareDetails)
    setErrors(found)
    if (hasPaymentErrors(found)) return
    mutation.mutate()
  }

  return (
    <PaymentSettingsView
      methods={methods}
      shareDetails={shareDetails}
      errors={errors}
      isDirty={isDirty}
      isSaving={mutation.isPending}
      saveError={mutation.isError ? saveErrorMessage(mutation.error) : null}
      onShareChange={(value) => {
        mutation.reset()
        setShareDetails(value)
      }}
      onMethodChange={(next) => {
        mutation.reset()
        setMethods((current) => current.map((method) => (method.id === next.id ? next : method)))
      }}
      onMethodRemove={(id) => setMethods((current) => current.filter((method) => method.id !== id))}
      onMethodAdd={() => setMethods((current) => [...current, newMethodDraft(current.length === 0 ? 'pago_movil' : 'otro')])}
      onSave={handleSave}
    />
  )
}

export default function PaymentSettingsForm() {
  const tenantId = useAuthStore((state) => state.tenantId)

  const query = useQuery({
    queryKey: paymentSettingsKey(tenantId),
    queryFn: async (): Promise<SavedPaymentSettings> => {
      const settings = await getBotSettings()
      return {
        methods: settings.checkoutConfig?.paymentMethods ?? [],
        shareDetails: settings.checkoutConfig?.sharePaymentDetails === true,
      }
    },
  })

  if (query.isPending) {
    return (
      <div className="space-y-4 p-4 sm:p-6" aria-busy="true" aria-label="Cargando configuración de pagos">
        <Skeleton height={96} className="w-full" />
        <Skeleton height={220} className="w-full" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <div role="alert" className="m-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 sm:m-6">
        <p className="flex items-center gap-2 text-sm font-medium text-rose-800">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          No pudimos cargar la configuración de pagos.
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
          Reintentar
        </Button>
      </div>
    )
  }

  return <PaymentSettingsEditor key={query.dataUpdatedAt} saved={query.data} tenantId={tenantId} />
}
